import { BaseApiService } from '@/shared/api/services/base.service';
import type { JsonApiResource } from '@/shared/api/services/base.service';
import type { Album } from '@/features/design/album/api/album-service';
import { mapSummary, type EstimateSummary } from './estimate-service';

/**
 * A running estimate (and album) of one design decision of a building
 * (docs/estimate/ADR-001). `display_title` / `album_display_title` are the
 * only names clients show; both are composed server-side.
 */
export interface DesignLine {
  id: string;
  display_title: string;
  album_display_title: string | null;
  is_updating: boolean;
  is_album_updating: boolean;
  currentEstimate: EstimateSummary | null;
  currentAlbum: Album | null;
}

/**
 * Where a design stands with respect to the building's estimates and albums
 * (GET /projects/{p}/concepts/{c}/design-line). Flags and texts are API-driven.
 */
export interface DesignLineContext {
  design_line_id: string | null;
  is_priced_for_this_design: boolean;
  status_text: string;
  suggested_design_line_id: string | null;
  is_album_for_this_design: boolean;
  album_status_text: string;
  /** Every line of the building, newest activity first. */
  lines: DesignLine[];
  /** The design's own line, resolved from `lines`. */
  line: DesignLine | null;
}

interface DesignLineAttributes {
  display_title: string;
  album_display_title: string | null;
  is_updating: boolean;
  is_album_updating: boolean;
}

type ContextAttributes = Omit<DesignLineContext, 'lines' | 'line'>;

interface RelationshipRef {
  data?: { id?: string } | null;
}

type AnyResource = JsonApiResource<Record<string, unknown>>;

function toAlbum(resource: AnyResource): Album {
  return { id: resource.id, ...(resource.attributes as Omit<Album, 'id'>) };
}

/** Lines in `resources`, each joined with its current estimate/album. */
function mapLines(lineResources: AnyResource[], included: AnyResource[]): DesignLine[] {
  const byKey = new Map(included.map((r) => [`${r.type}:${r.id}`, r]));
  return lineResources.map((resource) => {
    const relationships = resource.relationships as
      | Record<string, RelationshipRef | undefined>
      | undefined;
    const estimateId = relationships?.current_estimate?.data?.id;
    const albumId = relationships?.current_album?.data?.id;
    const estimate = estimateId ? byKey.get(`estimate:${estimateId}`) : undefined;
    const album = albumId ? byKey.get(`concept-album:${albumId}`) : undefined;
    const attributes = resource.attributes as unknown as DesignLineAttributes;
    return {
      id: resource.id,
      display_title: attributes.display_title,
      album_display_title: attributes.album_display_title,
      is_updating: attributes.is_updating,
      is_album_updating: attributes.is_album_updating,
      currentEstimate: estimate
        ? mapSummary(estimate as unknown as Parameters<typeof mapSummary>[0])
        : null,
      currentAlbum: album ? toAlbum(album) : null,
    };
  });
}

class DesignLineService extends BaseApiService {
  async context(projectId: string, conceptId: string): Promise<DesignLineContext> {
    const response = await this.httpGet<{
      data: JsonApiResource<ContextAttributes>;
      included?: AnyResource[];
    }>(`/v1/projects/${projectId}/concepts/${conceptId}/design-line`);
    const included = response.included ?? [];
    const lines = mapLines(
      included.filter((r) => r.type === 'design_line'),
      included
    );
    const attributes = response.data.attributes;
    return {
      ...attributes,
      lines,
      line: lines.find((l) => l.id === attributes.design_line_id) ?? null,
    };
  }

  async listByProject(projectId: string): Promise<DesignLine[]> {
    const response = await this.httpGet<{
      data: AnyResource[];
      included?: AnyResource[];
    }>(`/v1/projects/${projectId}/design-lines`);
    return mapLines(response.data, response.included ?? []);
  }

  /** Estimate versions of a line, newest first. */
  async estimateVersions(lineId: string): Promise<EstimateSummary[]> {
    const response = await this.httpGet<{ data: AnyResource[] }>(
      `/v1/design-lines/${lineId}/versions`
    );
    return response.data.map((r) =>
      mapSummary(r as unknown as Parameters<typeof mapSummary>[0])
    );
  }

  /** Album versions of a line, newest first. */
  async albumVersions(lineId: string): Promise<Album[]> {
    const response = await this.httpGet<{ data: AnyResource[] }>(
      `/v1/design-lines/${lineId}/albums`
    );
    return response.data.map(toAlbum);
  }

  async restoreEstimate(lineId: string, estimateId: string): Promise<void> {
    await this.post(
      `/v1/design-lines/${lineId}/restore`,
      this.formatJsonApiData('design_line', { estimate_id: estimateId })
    );
  }

  /**
   * Delete the whole running estimate: every estimate version and every album
   * of the line. 422 with a server message while an album of it is generating.
   */
  async deleteLine(lineId: string): Promise<void> {
    await this.httpDelete(`/v1/design-lines/${lineId}`);
  }

  async restoreAlbum(lineId: string, albumId: string): Promise<void> {
    await this.post(
      `/v1/design-lines/${lineId}/albums/restore`,
      this.formatJsonApiData('design_line', { album_id: albumId })
    );
  }
}

export const designLineApi = new DesignLineService();
